import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Zap,
  Code,
  Settings,
  Globe,
  Package,
  ArrowRight,
} from 'lucide-react';

const DocumentationPage: React.FC = () => {
  const sections = [
    {
      title: 'Getting Started',
      icon: Zap,
      items: [
        {
          title: 'Installation',
          description: 'Install the library via npm, yarn, or pnpm',
          content: `# NPM
npm install salla-address-manager

# Yarn
yarn add salla-address-manager

# PNPM
pnpm add salla-address-manager`,
        },
        {
          title: 'Quick Start - React',
          description: 'Get started with React in under 5 minutes',
          content: `import { useAddressForm } from 'salla-address-manager/react';

function AddressForm() {
  const form = useAddressForm({
    locale: 'en',
    validation: {
      validateOnChange: true,
      validateOnBlur: true
    }
  });

  return (
    <form onSubmit={form.handleSubmit}>
      <div>
        <label>Country</label>
        <select {...form.getFieldProps('country')}>
          {form.countries.map(country => (
            <option key={country.id} value={country.id}>
              {country.name}
            </option>
          ))}
        </select>
        {form.errors.country && (
          <span className="error">{form.errors.country}</span>
        )}
      </div>

      <div>
        <label>City</label>
        <input {...form.getFieldProps('city')} />
        {form.errors.city && (
          <span className="error">{form.errors.city}</span>
        )}
      </div>

      <div>
        <label>Street</label>
        <input {...form.getFieldProps('street')} />
        {form.errors.street && (
          <span className="error">{form.errors.street}</span>
        )}
      </div>

      <button type="submit" disabled={!form.isValid}>
        {form.isSubmitting ? 'Saving...' : 'Save Address'}
      </button>
    </form>
  );
}`,
        },
        {
          title: 'Quick Start - Vue 3',
          description: 'Get started with Vue 3 Composition API using the latest features',
          content: `<script setup lang="ts">
import { useAddressForm } from 'salla-address-manager/vue3';

const form = useAddressForm({
  locale: 'en',
  validateOnChange: true,
  validateOnBlur: true
});

// Use handleSubmit with callbacks for easy form submission
const handleSubmit = form.handleSubmit(
  async (data) => {
    // Valid data - transform to API format
    const payload = form.toAPIPayload({
      fieldMappings: {
        country: 'country_id',
        city: 'city_id',
        district: 'district_id'
      },
      includeNames: ['district'],
      defaults: {
        type: 'delivery',
        lat: 24.7136,
        lng: 46.6753
      }
    });
    
    await fetch('/api/addresses', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    alert('Address saved!');
  },
  (errors) => {
    console.error('Validation errors:', errors);
  }
);
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <!-- Using getFieldProps for automatic configuration -->
    <div>
      <label>Country</label>
      <select v-bind="form.getFieldProps('country')">
        <option value="">Select Country</option>
        <option 
          v-for="country in form.countries.value" 
          :key="country.id" 
          :value="country.id"
        >
          {{ country.name }}
        </option>
      </select>
      <span v-if="form.errors.value.country" class="error">
        {{ form.errors.value.country }}
      </span>
    </div>

    <!-- Cities auto-loaded when country changes -->
    <div>
      <label>City</label>
      <select v-bind="form.getFieldProps('city')">
        <option value="">
          {{ form.loading.value.cities ? 'Loading...' : 'Select City' }}
        </option>
        <option 
          v-for="city in form.currentCities.value" 
          :key="city.id" 
          :value="city.id"
        >
          {{ city.name }}
        </option>
      </select>
      <span v-if="form.errors.value.city" class="error">
        {{ form.errors.value.city }}
      </span>
    </div>

    <!-- Districts auto-loaded when city changes -->
    <div>
      <label>District</label>
      <select v-bind="form.getFieldProps('district')">
        <option value="">Select District</option>
        <option 
          v-for="district in form.currentDistricts.value" 
          :key="district.id" 
          :value="district.id"
        >
          {{ district.name }}
        </option>
      </select>
    </div>

    <div>
      <label>Street</label>
      <input v-bind="form.getFieldProps('street')" type="text" />
      <span v-if="form.errors.value.street" class="error">
        {{ form.errors.value.street }}
      </span>
    </div>

    <button type="submit" :disabled="!form.meta.value.isValid">
      {{ form.meta.value.isSubmitting ? 'Saving...' : 'Save Address' }}
    </button>
  </form>
</template>

// Key features:
// - getFieldProps: automatic field configuration
// - currentCities/Districts: auto-filtered based on selection
// - toAPIPayload: transform to API format
// - handleSubmit: easy form submission with callbacks
// - loading/meta: structured state API`,
        },
        {
          title: 'Quick Start - Vue 2',
          description: 'Get started with Vue 2 using mixins',
          content: `<template>
  <form @submit.prevent="handleSubmit">
    <!-- Using getFieldProps for automatic configuration -->
    <div>
      <label>Country</label>
      <select v-bind="getFieldProps('country')">
        <option value="">Select Country</option>
        <option 
          v-for="country in countries" 
          :key="country.id" 
          :value="country.id"
        >
          {{ country.name }}
        </option>
      </select>
      <span v-if="errors.country" class="error">
        {{ errors.country }}
      </span>
    </div>

    <!-- Cities auto-loaded when country changes -->
    <div>
      <label>City</label>
      <select v-bind="getFieldProps('city')">
        <option value="">
          {{ loading.cities ? 'Loading...' : 'Select City' }}
        </option>
        <option 
          v-for="city in currentCities" 
          :key="city.id" 
          :value="city.id"
        >
          {{ city.name }}
        </option>
      </select>
      <span v-if="errors.city" class="error">
        {{ errors.city }}
      </span>
    </div>

    <!-- Districts auto-loaded when city changes -->
    <div>
      <label>District</label>
      <select v-bind="getFieldProps('district')">
        <option value="">Select District</option>
        <option 
          v-for="district in currentDistricts" 
          :key="district.id" 
          :value="district.id"
        >
          {{ district.name }}
        </option>
      </select>
    </div>

    <div>
      <label>Street</label>
      <input v-bind="getFieldProps('street')" type="text" />
      <span v-if="errors.street" class="error">
        {{ errors.street }}
      </span>
    </div>

    <button type="submit" :disabled="!meta.isValid">
      {{ meta.isSubmitting ? 'Saving...' : 'Save Address' }}
    </button>
  </form>
</template>

<script>
import { AddressSystemMixin } from 'salla-address-manager/vue2';

export default {
  name: 'AddressForm',
  mixins: [AddressSystemMixin],
  
  created() {
    this.initializeAddressSystem({
      locale: 'en',
      validateOnChange: true,
      validateOnBlur: true
    });
  },
  
  methods: {
    async handleSubmit() {
      // Use handleSubmit for easy form submission
      const submitHandler = this.handleSubmit(
        async (data) => {
          // Transform to API format
          const payload = this.toAPIPayload({
            fieldMappings: {
              country: 'country_id',
              city: 'city_id',
              district: 'district_id'
            },
            includeNames: ['district'],
            defaults: {
              type: 'delivery',
              lat: 24.7136,
              lng: 46.6753
            }
          });
          
          await fetch('/api/addresses', {
            method: 'POST',
            body: JSON.stringify(payload)
          });
          
          alert('Address saved!');
        },
        (errors) => {
          console.error('Validation errors:', errors);
        }
      );
      
      await submitHandler();
    }
  }
};
</script>

// Available computed properties from mixin:
// - data: form data
// - errors: validation errors
// - fields: form fields
// - countries, cities, districts: async data
// - currentCities, currentDistricts: filtered data
// - loading: { form, countries, cities, districts }
// - meta: { isValid, isDirty, isSubmitting }
// - touched: touched fields`,
        },
        {
          title: 'Quick Start - Vanilla JavaScript',
          description: 'Use without any framework',
          content: `import { createAddressSystem } from 'salla-address-manager';

// Initialize the system
const system = createAddressSystem({
  locale: 'en',
  validation: {
    validateOnChange: true,
    debounceMs: 300
  }
});

// Create a form controller
const form = system.createFormController();

// Listen to state changes
form.on('form:stateChange', (event) => {
  const { data, errors, isValid } = event.data;
  
  // Update your UI
  updateFormUI(data, errors, isValid);
});

// Set field values
form.setFieldValue('country', 'SA');
form.setFieldValue('city', 'riyadh');
form.setFieldValue('street', '123 King Fahd Road');

// Submit the form
const success = await form.submit();

if (success) {
  console.log('Address saved successfully!');
}`,
        },
      ],
    },
    {
      title: 'Core Concepts',
      icon: BookOpen,
      items: [
        {
          title: 'Headless Architecture',
          description: 'Understand the headless pattern and its benefits',
          content: `The library follows a headless architecture, which means it provides:

1. **No UI Components**: You have complete control over the UI and styling
2. **Framework Agnostic**: Works with React, Vue, or vanilla JavaScript
3. **Data & Logic Only**: Handles state management, validation, and business logic
4. **Maximum Flexibility**: Integrate with any design system or component library

Benefits:
- Bring your own styling (Tailwind, CSS Modules, Styled Components, etc.)
- Use with any UI library (Material-UI, Ant Design, Chakra, etc.)
- Full control over accessibility (a11y)
- Perfect for design systems and custom UIs`,
        },
        {
          title: 'Form State Management',
          description: 'How form state is managed internally',
          content: `The library provides comprehensive form state management:

1. **Field Values**: Tracks all form field values
2. **Validation Errors**: Per-field and form-level errors
3. **Dirty/Touched States**: Know which fields have been modified
4. **Submission State**: Track form submission progress
5. **Loading States**: Handle async operations gracefully

Example state object:
{
  data: {
    country: 'SA',
    city: 'riyadh',
    street: '123 Main St',
    district: 'Al Malaz',
    description: 'Near the mall'
  },
  errors: {
    street: 'Street must be at least 5 characters'
  },
  meta: {
    isDirty: true,
    isTouched: {
      country: true,
      city: true,
      street: true
    }
  },
  isValid: false,
  isSubmitting: false
}`,
        },
        {
          title: 'Country-Specific Validation',
          description: 'Dynamic validation schemas for different countries',
          content: `Each country has its own validation schema with specific rules:

Saudi Arabia (SA):
- Country (required)
- City (required)
- District (required)
- Street (required, min 5 chars)
- Building Number (optional for national address)
- Postal Code (optional, 5 digits)
- Description (optional)

United States (US):
- Country (required)
- State (required)
- City (required)
- ZIP Code (required, 5 digits)
- Street (required)
- Apartment/Suite (optional)

United Arab Emirates (AE):
- Country (required)
- Emirate (required)
- Area (required)
- Street (required)
- Building/Villa (optional)

The library automatically applies the correct schema based on the selected country.`,
        },
      ],
    },
    {
      title: 'Advanced Features',
      icon: Settings,
      items: [
        {
          title: 'Async Data Fetching',
          description: 'Load countries and cities from external APIs',
          content: `import { createFetcherAdapter } from 'salla-address-manager/helpers';

const form = useAddressForm({
  locale: 'en',
  ...createFetcherAdapter({
    countryFetcher: async () => {
      const response = await fetch('/api/countries');
      return response.json();
    },
    cityFetcher: async (countryId) => {
      const response = await fetch(\`/api/countries/\${countryId}/cities\`);
      return response.json();
    },
    districtFetcher: async (cityId) => {
      const response = await fetch(\`/api/cities/\${cityId}/districts\`);
      return response.json();
    },
    responseTransform: {
      wrapper: 'data.data', // Extract data from response
      localizedName: true   // Use localized names
    }
  }, 'en')
});

// The library handles:
// - Caching of fetched data
// - Loading states
// - Error handling
// - Fallback to built-in data`,
        },
        {
          title: 'Saudi National Address Integration',
          description: 'Support for Saudi Arabia national address system',
          content: `import { useNationalAddressForm } from 'salla-address-manager/react';

function NationalAddressForm() {
  const form = useNationalAddressForm({
    locale: 'ar',
    enableNationalAddress: true,
    onNationalAddressSelect: (address) => {
      // Auto-fill form with selected national address
      console.log('Selected:', address);
    }
  });

  return (
    <form onSubmit={form.handleSubmit}>
      <div>
        <label>Short Address (National Address)</label>
        <input
          {...form.getFieldProps('nationalAddress')}
          placeholder="AAAA1234"
        />
        {form.nationalAddressData && (
          <div className="suggestions">
            {/* Display autocomplete suggestions */}
          </div>
        )}
      </div>

      <div>
        <label>Building Number</label>
        <input {...form.getFieldProps('buildingNumber')} />
      </div>

      <div>
        <label>Postal Code</label>
        <input {...form.getFieldProps('postalCode')} />
      </div>

      {/* Other fields */}
    </form>
  );
}`,
        },
        {
          title: 'Multi-Language Support',
          description: 'Built-in internationalization with Arabic and English',
          content: `import { useAddressForm } from 'salla-address-manager/react';

// English
const formEN = useAddressForm({ locale: 'en' });

// Arabic with RTL support
const formAR = useAddressForm({ locale: 'ar' });

// Dynamic language switching
function MultiLanguageForm() {
  const [locale, setLocale] = useState<'en' | 'ar'>('en');
  
  const form = useAddressForm({ locale });

  return (
    <div dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div>
        <button onClick={() => setLocale('en')}>English</button>
        <button onClick={() => setLocale('ar')}>العربية</button>
      </div>

      <form onSubmit={form.handleSubmit}>
        {/* Form fields with localized labels and errors */}
      </form>
    </div>
  );
}

// All validation messages are automatically localized
// Error messages appear in the selected language`,
        },
        {
          title: 'Custom Storage Adapters',
          description: 'Use your own storage backend',
          content: `import { createAddressSystem } from 'salla-address-manager';
import { MemoryStorageAdapter } from 'salla-address-manager';

// Built-in memory storage
const systemMemory = createAddressSystem({
  storage: new MemoryStorageAdapter()
});

// Custom API storage adapter
class APIStorageAdapter {
  async getAddresses() {
    const res = await fetch('/api/addresses');
    return res.json();
  }

  async addAddress(address) {
    const res = await fetch('/api/addresses', {
      method: 'POST',
      body: JSON.stringify(address)
    });
    return res.json();
  }

  async updateAddress(id, address) {
    const res = await fetch(\`/api/addresses/\${id}\`, {
      method: 'PUT',
      body: JSON.stringify(address)
    });
    return res.json();
  }

  async deleteAddress(id) {
    await fetch(\`/api/addresses/\${id}\`, {
      method: 'DELETE'
    });
  }
}

const systemAPI = createAddressSystem({
  storage: new APIStorageAdapter()
});`,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-12">
          <h1 className="text-4xl font-bold mb-4">Documentation</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Everything you need to know about using Salla Address Manager in your
            projects. From basic setup to advanced configurations.
          </p>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-8">
          <h2 className="font-bold text-lg mb-4">Quick Navigation</h2>
          <div className="flex flex-wrap gap-4">
            {sections.map((section) => (
              <a
                key={section.title}
                href={`#${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="btn btn-secondary text-sm flex items-center space-x-2"
              >
                <section.icon className="w-4 h-4" />
                <span>{section.title}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="container-custom section">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="card">
                <h3 className="font-bold mb-4">On This Page</h3>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <div key={section.title}>
                      <a
                        href={`#${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                        className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-salla-primary transition-colors"
                      >
                        <section.icon className="w-4 h-4" />
                        <span>{section.title}</span>
                      </a>
                      <ul className="ml-6 mt-2 space-y-1">
                        {section.items.map((item) => (
                          <li key={item.title}>
                            <a
                              href={`#${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                              className="text-sm text-gray-600 hover:text-salla-primary transition-colors"
                            >
                              {item.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </nav>
              </div>

              <div className="card bg-gradient-to-br from-salla-primary to-salla-secondary text-white">
                <h3 className="font-bold mb-2">Need Help?</h3>
                <p className="text-sm mb-4 text-white/90">
                  Check out our guides and examples for more detailed tutorials.
                </p>
                <Link
                  to="/guides"
                  className="inline-flex items-center space-x-2 text-sm font-medium text-white hover:underline"
                >
                  <span>View Guides</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-12">
            {sections.map((section) => (
              <section
                key={section.title}
                id={section.title.toLowerCase().replace(/\s+/g, '-')}
              >
                <div className="flex items-center space-x-3 mb-6">
                  <section.icon className="w-8 h-8 text-salla-primary" />
                  <h2 className="text-3xl font-bold">{section.title}</h2>
                </div>

                <div className="space-y-8">
                  {section.items.map((item) => (
                    <div
                      key={item.title}
                      id={item.title.toLowerCase().replace(/\s+/g, '-')}
                      className="card"
                    >
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-600 mb-4">{item.description}</p>

                      <div className="bg-gray-900 text-gray-100 p-6 rounded-lg overflow-x-auto">
                        <pre className="text-sm whitespace-pre-wrap">
                          <code>{item.content}</code>
                        </pre>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <section className="bg-white border-t border-gray-200">
        <div className="container-custom section">
          <h2 className="text-3xl font-bold text-center mb-8">Next Steps</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/examples" className="card hover:shadow-md transition-shadow">
              <Code className="w-12 h-12 text-salla-primary mb-4" />
              <h3 className="font-bold mb-2">View Examples</h3>
              <p className="text-sm text-gray-600 mb-4">
                Explore live, interactive examples with StackBlitz
              </p>
              <span className="text-salla-primary flex items-center space-x-1">
                <span>Explore</span>
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            <Link to="/api" className="card hover:shadow-md transition-shadow">
              <Package className="w-12 h-12 text-salla-primary mb-4" />
              <h3 className="font-bold mb-2">API Reference</h3>
              <p className="text-sm text-gray-600 mb-4">
                Detailed API documentation for all methods and types
              </p>
              <span className="text-salla-primary flex items-center space-x-1">
                <span>Read Docs</span>
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            <Link to="/guides" className="card hover:shadow-md transition-shadow">
              <Globe className="w-12 h-12 text-salla-primary mb-4" />
              <h3 className="font-bold mb-2">Guides</h3>
              <p className="text-sm text-gray-600 mb-4">
                In-depth guides for specific features and use cases
              </p>
              <span className="text-salla-primary flex items-center space-x-1">
                <span>Learn More</span>
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DocumentationPage;

